const WEEKDAY_BY_INDEX = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const PART_FORMATTER_OPTIONS = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    era: undefined,
};
const formatterCache = new Map();
const formatterFor = (timeZone) => {
    const cached = formatterCache.get(timeZone);
    if (cached) {
        return cached;
    }
    const created = new Intl.DateTimeFormat('en-US', {
        ...PART_FORMATTER_OPTIONS,
        timeZone,
    });
    formatterCache.set(timeZone, created);
    return created;
};
const readPart = (parts, type) => {
    const part = parts.find((candidate) => candidate.type === type);
    return part ? Number.parseInt(part.value, 10) : 0;
};
/**
 * Converts an instant into wall-clock fields for a given time zone.
 *
 * Uses the platform's time zone database rather than manual offset arithmetic,
 * so daylight saving transitions are handled by the same data the operating
 * system uses.
 */
export const toLocalDateTime = (instant, timeZone) => {
    const date = new Date(instant);
    const parts = formatterFor(timeZone).formatToParts(date);
    // `hour12: false` renders midnight as 24 in some ICU versions.
    const rawHour = readPart(parts, 'hour');
    return {
        year: readPart(parts, 'year'),
        month: readPart(parts, 'month'),
        day: readPart(parts, 'day'),
        hour: rawHour === 24 ? 0 : rawHour,
        minute: readPart(parts, 'minute'),
        second: readPart(parts, 'second'),
        dayOfWeek: weekdayFor(date, timeZone),
    };
};
const weekdayFor = (date, timeZone) => {
    const label = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(date);
    const index = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(label);
    const resolved = WEEKDAY_BY_INDEX[index === -1 ? 0 : index];
    // The lookup table covers every index the formatter can produce.
    return resolved ?? 'SUN';
};
/**
 * The calendar day a budget belongs to, as `YYYY-MM-DD` in the family's zone.
 *
 * SPEC.md left "daily budget" without a definition of "day". This is it: the
 * family's local calendar day, which keeps behaviour predictable when a device
 * travels.
 */
export const budgetDayKey = (local) => `${String(local.year).padStart(4, '0')}-${String(local.month).padStart(2, '0')}-${String(local.day).padStart(2, '0')}`;
/** Minutes elapsed since local midnight. */
export const minuteOfDay = (local) => local.hour * 60 + local.minute;
/** Seconds elapsed since local midnight. */
export const secondOfDay = (local) => minuteOfDay(local) * 60 + local.second;
/**
 * Parses an instant into epoch milliseconds.
 *
 * Instants are compared through this rather than lexicographically: two valid
 * ISO strings for the same moment can differ in fractional-second digits, which
 * makes string comparison quietly wrong.
 */
export const instantToEpochMs = (instant) => new Date(instant).getTime();
/** Parses `HH:mm` into minutes since midnight. */
export const timeOfDayToMinutes = (value) => {
    const [hours, minutes] = value.split(':');
    return Number.parseInt(hours ?? '0', 10) * 60 + Number.parseInt(minutes ?? '0', 10);
};
//# sourceMappingURL=calendar.js.map